import { EP_PACKAGE_DOCUMENTS } from './epAdvanceWorkflow.js';
import { cleanReportPromptText } from './reportPromptText.js';
import { epSurveyGroundTasks, mergeEpSurveyTasks, type EpSurveyTask } from './epSurveyTasks.js';

export const EP_SURVEY_REPORT_TYPE = 'EpSurveyPackageReport';
export const epSurveyReportId = (requestId: string) => `service-ep-survey-${requestId}`;
export const epSurveyReportRequestId = (reportId: string) => /^service-ep-survey-([a-zA-Z0-9_-]{1,128})$/.exec(reportId)?.[1] || '';

export type EpSurveyPackageReport = {
    reportType: typeof EP_SURVEY_REPORT_TYPE;
    reference: string;
    status: 'draft' | 'published';
    classification: string;
    profile: Array<{ label: string; value: string }>;
    documents: Record<string, string>;
    sourceGaps: Array<{ gap: string; finding: string }>;
    clientTasks: EpSurveyTask[];
    sources: Array<{ title: string; detail: string; url: string; datedAt: string }>;
    gatheredAt: string;
    signature: string;
    signedAt: string;
};

const text = (value: unknown, maximum = 24000) => String(value ?? '').trim().slice(0, maximum);
const published = (status: unknown) => ['delivered', 'invoiced', 'completed'].includes(text(status, 40));

export function epSurveyPackageReport(request: Record<string, any>): EpSurveyPackageReport {
    const v2 = request.epSurveyVersion === 2;
    const survey = cleanReportPromptText((v2 ? request.epSurveyV2 : request.epSurvey) || {});
    const intake = { ...(request.epSurveyIntake || {}), ...(survey.values || {}) };
    const research = survey.research || {};
    const profile = [
        ['Principal', intake.principalName],
        ['Event', intake.eventName],
        ['Event date', intake.eventDate],
        ['Venue', intake.venueName],
        ['Venue address', [intake.venueAddress || intake.addressLine1, intake.city, intake.venueState || intake.state, intake.venueCountry || intake.country].filter(Boolean).join(', ')],
        ['Threat assessment', intake.threatClass],
        ['Advance agents', intake.advanceAgentCount],
        ['Safe room required', intake.safeRoomRequired],
    ].map(([label, value]) => ({ label: String(label), value: text(value, 500) })).filter(item => item.value);
    const gaps: string[] = Array.isArray(research.gaps) ? research.gaps : [];
    const tasks = survey.clientTasks || mergeEpSurveyTasks(epSurveyGroundTasks(v2 ? 2 : 1, research), request.epSurveyTasks);
    const sources = ([['FCDO travel advisory', research.fcdo], ['U.S. State Department travel advisory', research.dos]] as const)
        .filter(([, source]) => source).map(([title, source]) => ({ title, detail: text(source.text, 3000),
            url: /^https?:\/\//i.test(text(source.url, 500)) ? text(source.url, 500) : '', datedAt: text(source.updatedAt, 80) }));
    return {
        reportType: EP_SURVEY_REPORT_TYPE, reference: text(request.requestReference, 80),
        status: published(request.status) ? 'published' : 'draft', classification: text(request.deliveryClassification, 40) || 'confidential',
        profile, documents: Object.fromEntries(EP_PACKAGE_DOCUMENTS.map(item => [item.key, text(survey.documents?.[item.key])])),
        sourceGaps: gaps.map(gap => ({ gap: text(gap, 1000), finding: text(survey.gapNotes?.[gap], 1000) || 'Unresolved after available desk research.' })),
        clientTasks: tasks.map((task: EpSurveyTask) => ({ id: text(task.id, 80), title: text(task.title, 500),
            status: task.status, note: text(task.note, 1000) })), sources, gatheredAt: text(research.gatheredAt, 80),
        signature: text(survey.qa?.signature, 200), signedAt: published(request.status) ? text(survey.qa?.signedAt, 80) : '',
    };
}

export function epSurveyDeliveryLink(requestId: string, request: Record<string, any>) {
    const survey = request.epSurveyVersion === 2 ? request.epSurveyV2 : request.epSurvey;
    const id = epSurveyReportId(requestId);
    const saved = Array.isArray(request.deliveryReports) && request.deliveryReports.some((report: any) => report.id === id && report.type === EP_SURVEY_REPORT_TYPE);
    if (!published(request.status) || !(['EP-11', 'S-11'].includes(text(request.serviceCode, 40)) || /advance.*site survey/i.test(text(request.serviceName, 200))) ||
        !saved && !EP_PACKAGE_DOCUMENTS.every(item => text(survey?.documents?.[item.key]).length >= 100))
        return null;
    return { id, sourceId: id, title: `${text(request.requestReference, 80)} Advance Site Survey`, type: EP_SURVEY_REPORT_TYPE };
}
